import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Inbox, MessageSquare, Paperclip, X, FileText, Image as ImageIcon, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dates";

const POLL_MS = 2500;
const MAX_BYTES = 8 * 1024 * 1024;

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [alsoEmail, setAlsoEmail] = useState(false);
  // iter-76: "Sending as" — owner/admin can send on behalf of any staff.
  const [staffList, setStaffList] = useState([]);
  const [senderUserId, setSenderUserId] = useState(user?.id || "");
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const canPickSender = user?.role === "owner" || user?.role === "admin";

  const loadThreads = () =>
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch((err) => toast.error(formatApiError(err)));

  const loadThread = (childId) =>
    api.get(`/messages/${childId}`).then((r) => setThread(r.data)).catch((err) => toast.error(formatApiError(err)));

  useEffect(() => {
    loadThreads().finally(() => setLoading(false));
    const id = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(id);
  }, []);

  // Load staff roster once for the "Sending as" dropdown (owner/admin only).
  useEffect(() => {
    if (!canPickSender) return;
    api.get("/staff/roster")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setStaffList(list.filter((s) => !s.pending_invite));
      })
      .catch(() => { /* dropdown just shows the current user */ });
  }, [canPickSender]);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const id = setInterval(() => loadThread(selected), POLL_MS);
    return () => clearInterval(id);
  }, [selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.length]);

  const activeThread = threads.find((t) => t.child_id === selected);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error("File is too large (max 8MB)"); return; }
    setAttachment(f);
  };

  const send = async (e) => {
    e.preventDefault();
    const txt = body.trim();
    if (!txt && !attachment) return;
    if (!selected) return;
    setSending(true);
    try {
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment);
        fd.append("body", txt);
        fd.append("also_email", alsoEmail ? "true" : "false");
        if (senderUserId && senderUserId !== user?.id) fd.append("sender_user_id", senderUserId);
        const res = await api.post(`/messages/${selected}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (alsoEmail) {
          if (res.data?.emailed_to) toast.success(`Sent + emailed to ${res.data.emailed_to}`);
          else if (res.data?.email_error) toast.warning(`Sent — email failed: ${res.data.email_error}`);
          else toast.success("Sent");
        } else toast.success("Sent");
      } else {
        const res = await api.post(`/messages/${selected}`, {
          body: txt,
          also_email: alsoEmail,
          sender_user_id: senderUserId && senderUserId !== user?.id ? senderUserId : undefined,
        });
        if (alsoEmail) {
          if (res.data?.emailed_to) toast.success(`Sent + emailed to ${res.data.emailed_to}`);
          else if (res.data?.email_error) toast.warning(`Sent — email failed: ${res.data.email_error}`);
          else toast.success("Sent");
        }
      }
      setBody("");
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
      await Promise.all([loadThread(selected), loadThreads()]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="messages-page" className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Parent ⇄ Staff</div>
        <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Messages</h1>
        <p className="mt-2 text-stone-500">One conversation per child. Attach PDFs/images and (optionally) email parents.</p>
      </header>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6 rounded-3xl border border-stone-200 bg-white overflow-hidden min-h-[540px]">
        <aside className="border-r border-stone-200 bg-stone-50" data-testid="threads-list">
          <div className="px-5 py-3 text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5 border-b border-stone-200">
            <Inbox className="w-3.5 h-3.5" /> Threads · {threads.length}
          </div>
          {threads.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">No messages yet. Parents will appear here when they reach out.</div>
          ) : threads.map((t) => {
            const active = t.child_id === selected;
            const unread = t.unread || 0;
            return (
              <button
                key={t.child_id}
                data-testid={`thread-item-${t.child_id}`}
                onClick={() => setSelected(t.child_id)}
                className={`w-full text-left px-5 py-4 border-b border-stone-200 transition ${active ? "bg-white" : "hover:bg-white"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-stone-900 text-sm truncate">{t.child_name}</div>
                  {unread > 0 && (
                    <span data-testid={`thread-unread-${t.child_id}`} className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-700 text-white">{unread}</span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-stone-500 truncate">
                  <span className={t.last_sender_role === "parent" ? "text-emerald-700 font-semibold" : ""}>{t.last_sender_role === "parent" ? "Parent: " : "You: "}</span>
                  {t.last_body}
                </div>
                <div className="mt-1 text-[10px] text-stone-400">{fmtDateTime(t.last_ts)}</div>
              </button>
            );
          })}
        </aside>

        <section className="flex flex-col min-h-[540px]">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-center text-stone-500 px-6">
              <div>
                <MessageSquare className="w-10 h-10 mx-auto text-stone-300" />
                <p className="mt-3 font-display text-xl text-stone-900">Pick a thread</p>
                <p className="mt-1 text-sm">Choose a child on the left to view and reply to messages.</p>
              </div>
            </div>
          ) : (
            <>
              <header className="px-6 py-4 border-b border-stone-200 flex items-baseline justify-between">
                <div>
                  <div className="font-display text-lg font-medium text-stone-900" data-testid="thread-title">{activeThread?.child_name || "Thread"}</div>
                  <div className="text-[11px] text-stone-500">{activeThread?.parent_email || ""}</div>
                </div>
                <div className="text-[11px] text-stone-400">{thread.length} message{thread.length === 1 ? "" : "s"}</div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="thread-messages" style={{ maxHeight: 460 }}>
                {thread.length === 0 ? (
                  <p className="text-center text-sm text-stone-500 py-10">No messages yet in this thread.</p>
                ) : thread.map((m) => (
                  <MessageBubble
                    key={m.id}
                    msg={m}
                    currentUserId={user?.id}
                    canModerate={true}
                    onChanged={async () => { await Promise.all([loadThread(selected), loadThreads()]); }}
                  />
                ))}
              </div>

              <form onSubmit={send} className="border-t border-stone-200 p-4 space-y-2" data-testid="thread-compose">
                {attachment && (
                  <div data-testid="attachment-preview" className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-stone-100 border border-stone-200">
                    <Paperclip className="w-3.5 h-3.5 text-emerald-700" />
                    <span className="truncate flex-1 font-medium">{attachment.name}</span>
                    <span className="text-stone-500">{Math.round(attachment.size / 1024)} KB</span>
                    <button type="button" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-stone-400 hover:text-stone-700">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="application/pdf,image/*,text/plain" data-testid="thread-attach-input" onChange={pickFile} className="hidden" />
                  <Button type="button" onClick={() => fileRef.current?.click()} data-testid="thread-attach-btn" variant="outline" className="h-11 px-3 rounded-xl">
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Input
                    data-testid="thread-input"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={`Reply as ${staffList.find((s) => s.id === senderUserId)?.name || user?.name || "staff"}…`}
                    className="h-11"
                    maxLength={4000}
                  />
                  <Button type="submit" data-testid="thread-send-btn" disabled={sending || (!body.trim() && !attachment)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
                    {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send
                  </Button>
                </div>
                {canPickSender && staffList.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-stone-600 px-1">
                    <span className="uppercase tracking-widest text-[10px] font-semibold text-emerald-700">Sending as</span>
                    <select
                      data-testid="thread-sender-picker"
                      value={senderUserId || user?.id || ""}
                      onChange={(e) => setSenderUserId(e.target.value)}
                      className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs focus:ring-emerald-600 focus:border-emerald-600"
                    >
                      {staffList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.id === user?.id ? " (you)" : ""} · {s.role}
                        </option>
                      ))}
                    </select>
                    {senderUserId && senderUserId !== user?.id && (
                      <span className="text-[10px] text-stone-400">
                        (parents will see the daycare name; “{staffList.find((s) => s.id === senderUserId)?.name}” shows as byline)
                      </span>
                    )}
                  </label>
                )}
                <label className="flex items-center gap-2 text-xs text-stone-600 px-1">
                  <input
                    type="checkbox"
                    data-testid="thread-also-email"
                    checked={alsoEmail}
                    onChange={(e) => setAlsoEmail(e.target.checked)}
                    className="rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                  />
                  Also email the parent {activeThread?.parent_email ? <span className="text-emerald-700 font-medium">({activeThread.parent_email})</span> : <span className="text-stone-400 italic">(no email on file)</span>}
                </label>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function MessageBubble({ msg, currentUserId, canModerate, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body || "");
  const [busy, setBusy] = useState(false);
  const mine = msg.sender_role === "staff";

  // Sender can always edit/delete their own; owner/admin can moderate anyone in the center.
  const own = msg.sender_id === currentUserId;
  const canEdit = own || canModerate;
  const canDelete = own || canModerate;

  const save = async () => {
    const text = draft.trim();
    if (!text) { toast.error("Message cannot be empty"); return; }
    setBusy(true);
    try { await api.put(`/messages/${msg.id}`, { body: text }); toast.success("Updated."); setEditing(false); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setBusy(true);
    try { await api.delete(`/messages/${msg.id}`); toast.success("Deleted."); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
      <div className={`relative max-w-md rounded-2xl px-4 py-2.5 ${mine ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-900"}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">
          {msg.sender_role === "parent" ? "Parent" : msg.sender_name || "Staff"}
        </div>

        {editing ? (
          <div className="mt-1 space-y-1">
            <textarea
              data-testid={`message-edit-input-${msg.id}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full text-sm rounded-lg border border-stone-300 px-2 py-1.5 text-stone-900 bg-white"
              rows={3}
              maxLength={4000}
              autoFocus
            />
            <div className="flex gap-1">
              <Button type="button" data-testid={`message-edit-save-${msg.id}`} disabled={busy} onClick={save} className="h-7 px-2.5 text-xs bg-white text-emerald-800 hover:bg-stone-50">
                <Check className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setEditing(false); setDraft(msg.body || ""); }} className={`h-7 px-2.5 text-xs ${mine ? "text-white hover:bg-emerald-800" : ""}`}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          msg.body && <div className="mt-1 text-sm whitespace-pre-line">{msg.body}</div>
        )}

        {msg.attachment_id && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/messages/attachments/${msg.attachment_id}`}
            target="_blank" rel="noopener noreferrer"
            data-testid={`attachment-link-${msg.id}`}
            className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${mine ? "bg-emerald-800/40 hover:bg-emerald-800/60" : "bg-white border border-stone-200 hover:bg-stone-50"}`}
          >
            {(msg.attachment_content_type || "").startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
            {msg.attachment_filename || "attachment"}
          </a>
        )}

        <div className="mt-1 flex items-center gap-1.5 text-[10px] opacity-60">
          <span>{fmtDateTime(msg.ts || msg.created_at)}</span>
          {msg.edited_at && <span className="italic">· edited</span>}
        </div>

        {!editing && (canEdit || canDelete) && (
          <div className={`absolute -top-2 ${mine ? "left-2" : "right-2"} flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
            {canEdit && !msg.attachment_id && (
              <button
                type="button"
                data-testid={`message-edit-${msg.id}`}
                onClick={() => setEditing(true)}
                title="Edit message"
                className="h-6 w-6 grid place-items-center rounded-full bg-white border border-stone-200 text-stone-600 hover:text-stone-900 hover:border-stone-300 shadow-sm"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                data-testid={`message-delete-${msg.id}`}
                onClick={remove}
                title="Delete message"
                className="h-6 w-6 grid place-items-center rounded-full bg-white border border-stone-200 text-rose-600 hover:text-rose-700 hover:border-rose-300 shadow-sm"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
